/**
 * AppHostGate — on app.* domains, everything requires a signed-in user.
 * Anonymous visitors are redirected to /login (with ?next= return path).
 * Root/sales domains and the dev preview are unaffected.
 *
 * PUBLIC_HOSTS below are app.* hosts that we've explicitly opened up for
 * anonymous browsing so search engines + LLM answer-engines can index
 * them and shoppers can browse/buy without a login gate. Auth is still
 * REQUIRED for member-only routes (`/host`, `/orders`, etc.) via each
 * page's own guard — this component just stops blanket-redirecting.
 */
import { useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const PUBLIC_PATHS = [
  "/login", "/register", "/forgot-password", "/reset-password",
  "/login/magic", "/login/handoff", "/terms", "/privacy", "/about", "/reviews/respond",
  "/property/calculator", "/diy/calculator",
  "/roadtrip", "/finder", "/roadtrip/drive",
  "/mic-test",
  "/cloudflare-setup",
  // HustleShield public forms — anonymous respondents must be able to
  // open and submit a form without authenticating (iter252 bugfix).
  "/form",
  // Public directory + POI/hotel discovery — the whole point is anonymous browsing.
  "/directory", "/hotels", "/finder/poi", "/discover",
  "/stops", "/suite-polish",
  // Tenant microsites — anonymous public read of migrated-brand sites
  // (e.g. polish-foodies). Owner's shop + recipes + contact must be
  // crawlable without a signup wall.
  "/sites",
  // HustleMeet Circles + Matchmaker sales pages (prefix matches also
  // open the individual circle detail page which is intentionally
  // public for discovery). Member-only routes under /hustlemeet/*
  // (profile, host/mine, events/*) still require auth via their own
  // page-level guards or the fall-through below.
  "/circles", "/matchmaker",
];

// Exact-match public paths — no subroute is opened.
// /meet is the public sales landing; sub-routes like /meet/profile
// or /meet/host/mine remain gated.
const PUBLIC_EXACT_PATHS = new Set([
  "/meet",
]);

// Fully-public app.* hosts — no auth required on any path. Sales-critical
// storefronts + product catalogues that MUST be crawlable/purchasable
// without a signup wall.
const PUBLIC_HOSTS = new Set([
  "app.hustleplaza.au",
  "app.tackle.au",
  "tackle.au",
  "app.hustleshop.au",
  "hustleshop.au",
  // HustleFinder — kids games, Sudoku, toilets, parks, playgrounds and
  // POIs. Entire experience is free and unauth'd by product decision.
  // Canonical host is hustlefinder.au (with "er"); the shorter
  // hustlefind.au 301s to it, so we allow-list both spellings on both
  // the bare and app.* subdomains.
  "hustlefinder.au",
  "app.hustlefinder.au",
  "hustlefind.au",
  "app.hustlefind.au",
  // HustleDirectory — public business directory. Bare + app.* must be
  // crawlable and browsable without login so search engines and AI
  // answer-engines can index every vendor page. Member-only routes
  // under /account/* still require auth via their own guards.
  "hustledirectory.au",
  "www.hustledirectory.au",
  "app.hustledirectory.au",
  // HustleTickets — public event browser + landing must be crawlable
  // and buyable without an account. Sprint 1 launch.
  "hustletickets.au",
  "app.hustletickets.au",
  "www.hustletickets.au",
  // HustleCircles + HustleMatchmaker — dedicated marketing domains
  // that route to the existing sales pages. Fully public for SEO.
  "hustlecircles.au",
  "app.hustlecircles.au",
  "www.hustlecircles.au",
  "hustlematchmaker.au",
  "app.hustlematchmaker.au",
  "www.hustlematchmaker.au",
]);

function isAppHost() {
  if (typeof window === "undefined") return false;
  return window.location.hostname.toLowerCase().startsWith("app.");
}

export const AppHostGate = ({ children }) => {
  const { user, checking } = useAuth();
  const { pathname } = useLocation();

  if (!isAppHost()) return children;
  const host = window.location.hostname.toLowerCase();
  // Fully-public hosts — indexable + shoppable without login.
  if (PUBLIC_HOSTS.has(host)) return children;
  // Public sales pages served under app.* hosts. Each of these mounts
  // a marketing/utility page that shouldn't require auth. Keep this
  // list narrow — everything else on app.* still needs a signed-in
  // member.
  if (pathname === "/" && (
        host.includes("hustlesuite") ||
        host === "app.hustletravel.au" ||
        host === "app.hustledictate.au" ||
        host === "app.hustleanswer.au"
      )) {
    return children;
  }
  if (PUBLIC_EXACT_PATHS.has(pathname)) return children;
  if (PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/")))
    return children;
  if (checking) return null;
  if (!user) {
    const returnTo = encodeURIComponent(pathname + window.location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }
  return children;
};

export default AppHostGate;
